import React, { useState, useEffect } from "react";
import { useAuth } from "../Auth/AuthContext";
import BASE_URL from "../../config/config";
import { jwtDecode } from "jwt-decode";
import Captcha from "../Auth/Captcha";
import "../../css/Comments.module.css";

const CommentsSection = ({ courseId, postId, contentType = "course" }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [formData, setFormData] = useState({
    comment_text: "",
    user_name: "",
    user_email: "",
    mobile: "",
  });
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");
  const [guestToken, setGuestToken] = useState(
    localStorage.getItem("guestToken")
  );
  const [purchaseStatus, setPurchaseStatus] = useState(null);
  const [guestReplyInfo, setGuestReplyInfo] = useState({});
  const [mainCommentSuccessMessage, setMainCommentSuccessMessage] =
    useState(null);
  const [replySuccessMessage, setReplySuccessMessage] = useState({});
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [replyCaptchaVerified, setReplyCaptchaVerified] = useState({});
  const [rating, setRating] = useState(5);

  const contentId = contentType === "post" ? postId : courseId;

  const commentSettings = {
    visibility: process.env.REACT_APP_COMMENT_VISIBILITY || "all_users",
    posting: process.env.REACT_APP_COMMENT_POSTING || "all_users",
    replies: process.env.REACT_APP_COMMENT_REPLIES || "enabled",
    guestCommenting: process.env.REACT_APP_GUEST_COMMENTING || "enabled",
  };

  // برای مقالات، همه کاربران می‌توانند نظرات را ببینند
  const canViewArticles = contentType === "post";

  const isGuestTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  };

  const getGuestToken = async () => {
    if (
      (!guestToken || isGuestTokenExpired(guestToken)) &&
      commentSettings.guestCommenting === "enabled" &&
      !user
    ) {
      try {
        const res = await fetch(`${BASE_URL}/guest-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "CSRF-Token": csrfToken,
          },
          credentials: "include",
        });
        const data = await res.json();
        localStorage.setItem("guestToken", data.token);
        setGuestToken(data.token);
      } catch (err) {
        console.error("Error fetching guest token:", err);
      }
    }
  };

  const fetchComments = async () => {
    try {
      const headers = {};
      if (user)
        headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
      else if (guestToken && !isGuestTokenExpired(guestToken))
        headers.Authorization = `Bearer ${guestToken}`;

      const url =
        contentType === "post"
          ? `${BASE_URL}/posts/${contentId}/comments?with_replies=true`
          : `${BASE_URL}/courses/${contentId}/comments?with_replies=true`;
  
      

      const res = await fetch(url, { headers });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      if (data.status === "success") {
        setComments(data.data || []);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      setError("خطا در دریافت نظرات");
    } finally {
      setLoading(false);
    }
  };

  const fetchCsrfToken = async () => {
    try {
      const res = await fetch(`${BASE_URL}/csrf-token`, {
        credentials: "include",
      });
      const data = await res.json();
      setCsrfToken(data.csrfToken);
    } catch (err) {
      console.error("CSRF error:", err);
    }
  };

  const checkPurchase = async () => {
    if (!user || contentType === "post") return; // برای مقالات نیازی به بررسی خرید نیست
    try {
      const res = await fetch(`${BASE_URL}/user/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      if (data.status === "success") setPurchaseStatus("completed");
    } catch (err) {
      console.error("Purchase check error:", err);
    }
  };

  useEffect(() => {
    fetchCsrfToken();
  }, []);

  useEffect(() => {
    getGuestToken();
  }, [csrfToken, user]);

  useEffect(() => {
    if (contentId) {
      fetchComments();
    }
  }, [contentId, guestToken, user, contentType]);

  useEffect(() => {
    if (contentType === "course") {
      checkPurchase();
    }
  }, [user, contentType]);

  const canView =
    contentType === "post"
      ? true // برای مقالات همه می‌توانند ببینند
      : commentSettings.visibility === "all_users" ||
        (commentSettings.visibility === "buyers_only" &&
          purchaseStatus === "completed");

  const canPost =
    contentType === "post"
      ? !!user // برای مقالات فقط کاربران عضو می‌توانند کامنت بگذارند
      : (commentSettings.posting === "all_users" &&
          (user || commentSettings.guestCommenting === "enabled")) ||
        (commentSettings.posting === "buyers_only" &&
          purchaseStatus === "completed");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (newRating) => {
    setRating(newRating);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMainCommentSuccessMessage(null);

    if (!captchaVerified) {
      setError("لطفاً ابتدا کد امنیتی را تأیید کنید.");
      return;
    }

    if (!formData.comment_text.trim()) {
      setError("متن نظر نمی‌تواند خالی باشد");
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "CSRF-Token": csrfToken,
      };
      if (user)
        headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
      else if (guestToken && contentType !== "post")
        // برای مقالات فقط کاربران عضو
        headers.Authorization = `Bearer ${guestToken}`;

      const payload = {
        comment_text: formData.comment_text,
        rating: rating,
        ...((user?.isGuest || !user) && contentType !== "post"
          ? {
              user_name: formData.user_name,
              user_email: formData.user_email,
              mobile: formData.mobile,
            }
          : {}),
      };

      const res = await fetch(
        `${BASE_URL}/${
          contentType === "post" ? "posts" : "courses"
        }/${contentId}/comments`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        setComments([data.data, ...comments]);
        setFormData({
          comment_text: "",
          user_name: "",
          user_email: "",
          mobile: "",
        });
        setMainCommentSuccessMessage("نظر شما با موفقیت ثبت شد!");
        setCaptchaVerified(false);
        setRating(5);
        setTimeout(() => setMainCommentSuccessMessage(null), 5000);
      } else {
        setError(data.message || "خطا در ثبت نظر");
      }
    } catch (err) {
      if (err.message.includes("jwt expired")) {
        localStorage.removeItem("guestToken");
        setGuestToken(null);
        setError("توکن مهمان منقضی شده. لطفاً صفحه را رفرش کنید.");
      } else {
        setError("خطا در ارسال نظر");
      }
    }
  };

  const handleReplySubmit = async (e, parentId) => {
    e.preventDefault();
    setError(null);
    setReplySuccessMessage((prev) => ({ ...prev, [parentId]: null }));

    if (!replyCaptchaVerified[parentId]) {
      setError("لطفاً ابتدا کد امنیتی را تأیید کنید.");
      return;
    }

    if (!replyText[parentId] || replyText[parentId].trim().length < 5) {
      setError("متن پاسخ باید حداقل ۵ کاراکتر باشد");
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "CSRF-Token": csrfToken,
      };

      if (user) {
        headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
      } else if (guestToken && contentType !== "post") {
        headers.Authorization = `Bearer ${guestToken}`;
      }

      const payload = {
        comment_text: replyText[parentId],
        parent_id: parentId,
        ...(!user &&
          contentType !== "post" && {
            user_name: guestReplyInfo[parentId]?.name || "",
            user_email: guestReplyInfo[parentId]?.email || "",
            mobile: guestReplyInfo[parentId]?.mobile || "",
          }),
      };

      const res = await fetch(
        `${BASE_URL}/${
          contentType === "post" ? "posts" : "courses"
        }/${contentId}/comments`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        const newReply = data.data;
        setComments((prev) =>
          prev.map((comment) =>
            comment.comment_id === parentId
              ? {
                  ...comment,
                  replies: [...(comment.replies || []), newReply],
                }
              : comment
          )
        );

        setReplyText((prev) => ({ ...prev, [parentId]: "" }));
        setGuestReplyInfo((prev) => ({ ...prev, [parentId]: {} }));
        setReplyingTo(null);
        setReplySuccessMessage((prev) => ({
          ...prev,
          [parentId]: "پاسخ شما با موفقیت ثبت شد!",
        }));
        setReplyCaptchaVerified((prev) => ({ ...prev, [parentId]: false }));
        setTimeout(
          () =>
            setReplySuccessMessage((prev) => ({ ...prev, [parentId]: null })),
          5000
        );
      } else {
        setError(data.message || "خطا در ارسال پاسخ");
      }
    } catch (err) {
      setError("خطا در ارسال پاسخ");
      console.error(err);
    }
  };

  const getInitials = (name) => {
    if (!name) return "ن";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2);
  };

  const getGradientClass = (index) => {
    const gradients = [
      "bg-gradient-to-br from-primary to-dark",
      "bg-gradient-to-br from-purple-500 to-pink-500",
      "bg-gradient-to-br from-blue-500 to-indigo-600",
      "bg-gradient-to-br from-green-500 to-teal-600",
      "bg-gradient-to-br from-yellow-500 to-orange-500",
    ];
    return gradients[index % gradients.length];
  };

  const renderStars = (rating) => {
    return "⭐".repeat(rating) + "☆".repeat(5 - rating);
  };

  const renderComments = (list, level = 0) =>
    list.map((commentItem, index) => (
      <div
        key={commentItem.comment_id}
        className={`bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 ${
          level > 0 ? "mr-6" : ""
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 ${getGradientClass(
                index
              )} rounded-full flex items-center justify-center text-white font-bold text-lg`}
            >
              {getInitials(commentItem.user?.name || commentItem.user_name)}
            </div>
            <div>
              <h4 className="font-bold text-dark">
                {commentItem.user?.name || commentItem.user_name || "ناشناس"}
              </h4>
              <p className="text-sm text-gray-500">
                {new Date(commentItem.created_at).toLocaleDateString("fa-IR")}
              </p>
            </div>
          </div>
          {contentType === "course" && (
            <div className="flex text-lg">
              {renderStars(commentItem.rating || 5)}
            </div>
          )}
        </div>
        <p className="text-gray-700 leading-relaxed pr-2 border-r-4 border-primary border-opacity-20">
          {commentItem.comment_text}
        </p>

        {commentSettings.replies === "enabled" && canPost && (
          <button
            className="mt-4 text-primary hover:text-dark transition-colors"
            onClick={() =>
              setReplyingTo(
                replyingTo === commentItem.comment_id
                  ? null
                  : commentItem.comment_id
              )
            }
          >
            {replyingTo === commentItem.comment_id ? "لغو پاسخ" : "پاسخ"}
          </button>
        )}

        {replyingTo === commentItem.comment_id && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <textarea
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent mb-3"
              value={replyText[commentItem.comment_id] || ""}
              onChange={(e) =>
                setReplyText((prev) => ({
                  ...prev,
                  [commentItem.comment_id]: e.target.value,
                }))
              }
              placeholder="پاسخ خود را بنویسید..."
              rows="3"
            />
            {!user && contentType !== "post" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="نام شما (اختیاری)"
                  value={guestReplyInfo[commentItem.comment_id]?.name || ""}
                  onChange={(e) =>
                    setGuestReplyInfo((prev) => ({
                      ...prev,
                      [commentItem.comment_id]: {
                        ...prev[commentItem.comment_id],
                        name: e.target.value,
                      },
                    }))
                  }
                />
                <input
                  type="email"
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="ایمیل شما (اختیاری)"
                  value={guestReplyInfo[commentItem.comment_id]?.email || ""}
                  onChange={(e) =>
                    setGuestReplyInfo((prev) => ({
                      ...prev,
                      [commentItem.comment_id]: {
                        ...prev[commentItem.comment_id],
                        email: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            )}

            <Captcha
              onVerify={(isVerified) =>
                setReplyCaptchaVerified((prev) => ({
                  ...prev,
                  [commentItem.comment_id]: isVerified,
                }))
              }
              onExpire={() =>
                setReplyCaptchaVerified((prev) => ({
                  ...prev,
                  [commentItem.comment_id]: false,
                }))
              }
              timeout={120000}
              regenerateOnError={true}
            />

            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={(e) => {
                  handleReplySubmit(e, commentItem.comment_id);
                }}
                className="px-4 py-2 bg-primary text-white rounded-xl font-medium hover:bg-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!replyCaptchaVerified[commentItem.comment_id]}
              >
                ارسال پاسخ
              </button>
              {replySuccessMessage[commentItem.comment_id] && (
                <span className="text-green-600 text-sm">
                  {replySuccessMessage[commentItem.comment_id]}
                </span>
              )}
            </div>
          </div>
        )}

        {commentItem.replies?.length > 0 && (
          <div className="mt-6 space-y-6">
            {renderComments(commentItem.replies, level + 1)}
          </div>
        )}
      </div>
    ));

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Reviews Header */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold gradient-text mb-4">
              {contentType === "post" ? "نظرات کاربران" : "نظرات دانشجویان"}
            </h3>
            <p className="text-gray-600 text-lg">
              {contentType === "post"
                ? "تجربه دیگران را بخوانید و دیدگاه خود را به اشتراک بگذارید"
                : "تجربه دیگران را بخوانید و از تصمیم‌گیری بهتری برخوردار باشید"}
            </p>
          </div>

          {!canView ? (
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center">
              <p className="text-gray-700">
                برای مشاهده نظرات باید دوره را خریداری کرده باشید.
              </p>
            </div>
          ) : loading ? (
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center">
              <p className="text-gray-700">در حال بارگذاری نظرات...</p>
            </div>
          ) : (
            <>
              {/* Reviews List */}
              <div className="space-y-6 mb-16">
                {comments.length > 0 ? (
                  renderComments(comments)
                ) : (
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center">
                    <p className="text-gray-700">هنوز نظری ثبت نشده است.</p>
                  </div>
                )}
              </div>

              {canPost && (
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  <h3 className="text-2xl font-bold gradient-text mb-6 text-center">
                    نظر خود را بنویسید
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {contentType !== "post" && !user && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-group">
                          <label
                            className="block text-gray-700 mb-2 font-medium"
                            htmlFor="user_name"
                          >
                            نام *
                          </label>
                          <input
                            type="text"
                            id="user_name"
                            name="user_name"
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="نام شما"
                            value={formData.user_name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label
                            className="block text-gray-700 mb-2 font-medium"
                            htmlFor="user_email"
                          >
                            ایمیل *
                          </label>
                          <input
                            type="email"
                            id="user_email"
                            name="user_email"
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="example@email.com"
                            value={formData.user_email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    )}

                    {contentType === "course" && (
                      <div className="form-group">
                        <label className="block text-gray-700 mb-2 font-medium">
                          امتیاز شما *
                        </label>
                        <div className="rating-input flex justify-end gap-1">
                          {[5, 4, 3, 2, 1].map((star) => (
                            <span
                              key={star}
                              className={`rating-star text-2xl cursor-pointer ${
                                rating >= star
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                              onClick={() => handleRatingChange(star)}
                            >
                              ⭐
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <label
                        className="block text-gray-700 mb-2 font-medium"
                        htmlFor="comment_text"
                      >
                        نظر شما *
                      </label>
                      <textarea
                        id="comment_text"
                        name="comment_text"
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="نظر خود را بنویسید..."
                        rows="4"
                        value={formData.comment_text}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <Captcha
                      onVerify={setCaptchaVerified}
                      onExpire={() => setCaptchaVerified(false)}
                      timeout={120000}
                      regenerateOnError={true}
                    />

                    <button
                      type="submit"
                      className="w-full bg-primary text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:bg-dark disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!captchaVerified}
                    >
                      💬 ارسال نظر
                    </button>

                    {mainCommentSuccessMessage && (
                      <div className="text-center text-green-600 mt-3">
                        {mainCommentSuccessMessage}
                      </div>
                    )}
                  </form>
                </div>
              )}

              {contentType === "post" && !user && (
                <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 text-center">
                  <p className="text-gray-700 mb-3">
                    برای ثبت نظر باید وارد حساب کاربری خود شوید.
                  </p>
                  <button
                    onClick={() => (window.location.href = "/login")}
                    className="px-6 py-2 bg-primary text-white rounded-xl font-medium hover:bg-dark transition-colors"
                  >
                    ورود به حساب کاربری
                  </button>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="mt-6 p-4 bg-red-100 text-red-700 rounded-xl text-center">
              {error}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CommentsSection;
